import React, {useEffect, useMemo, useRef, useState} from "react";
import {findParentInTree, getOptionName, toArray} from "../../utils/selectbox/selectorUtils";
import {ChevronDownIcon} from "./SelectorIcons";

/**
 * CodeCreateModal 내 부모 코드 선택 (gov_front RecursiveSelectView)
 */
function RecursiveParentSelect({data, value, onChange, label = "부모 선택"}) {
    const roots = data ?? [];
    const [open, setOpen] = useState(false);
    const [parent, setParent] = useState(null);
    const [selected, setSelected] = useState(null);
    const ref = useRef(null);

    const listToRender = useMemo(() => {
        if (parent) return toArray(parent.children);
        return roots;
    }, [parent, roots]);

    useEffect(() => {
        if (!value?.id || !roots.length) {
            setSelected(null);
            return;
        }
        const findById = (nodes, id) => {
            for (const n of nodes) {
                if (String(n.id) === String(id)) return n;
                const children = toArray(n.children);
                if (children.length) {
                    const found = findById(children, id);
                    if (found) return found;
                }
            }
            return undefined;
        };
        setSelected(findById(roots, value.id) ?? value);
    }, [value, roots]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleBack = () => {
        if (!parent) return;
        const parentOfCurrent = findParentInTree(roots, parent);
        setParent(parentOfCurrent ?? null);
    };

    const handleSelect = (option) => {
        setSelected(option);
        onChange?.(option);
        setOpen(false);
    };

    const handleDrillDown = (option) => {
        if (toArray(option.children).length > 0) {
            setParent(option);
        }
    };

    return (
        <div ref={ref} className="recursive-parent-select">
            <div className="recursive-parent-select-trigger" onClick={() => setOpen((p) => !p)}>
                <span className={selected ? "is-selected" : "is-placeholder"}>
                    {selected ? getOptionName(selected) : label}
                </span>
                <ChevronDownIcon style={{width: "12px", opacity: 0.8}}/>
            </div>

            {open && (
                <div className="recursive-parent-select-dropdown">
                    <div className="recursive-parent-select-header">
                        <span className="text-[12px] font-semibold text-[#6b7280]">
                            {parent ? getOptionName(parent) : label}
                        </span>
                        {parent && (
                            <button type="button" className="selector-drill-btn" onClick={handleBack} title="뒤로">
                                ←
                            </button>
                        )}
                    </div>
                    <div className="recursive-parent-select-list">
                        {listToRender.length > 0 ? (
                            listToRender.map((option) => {
                                const hasChildren = toArray(option.children).length > 0;
                                return (
                                    <div key={option.id} className="recursive-parent-select-option">
                                        <button
                                            type="button"
                                            className="recursive-parent-select-option-label"
                                            onClick={() => handleSelect(option)}
                                        >
                                            {getOptionName(option)}
                                        </button>
                                        {hasChildren && (
                                            <button
                                                type="button"
                                                className="selector-drill-btn"
                                                onClick={() => handleDrillDown(option)}
                                                title="하위"
                                            >
                                                ›
                                            </button>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="selector-empty">데이터 없음</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default RecursiveParentSelect;
